import { Plus, RefreshCw, Search, SlidersHorizontal } from 'lucide-react'
import { useMemo, useState } from 'react'

import { useCarriers, useDashboard, useProjects } from '../api/client'
import {
  AddParcelDialog,
  inputBase,
  inputClass,
  primaryButton,
} from '../components/AddParcelDialog'
import { ParcelDrawer } from '../components/ParcelDrawer'
import { ParcelRow } from '../components/ParcelRow'
import { GroupHeading, Section } from '../components/Section'
import { TodayStrip } from '../components/TodayStrip'
import { de } from '../i18n/de'
import { DAY_GROUP_ORDER, dayGroup, needsAttentionToday, type DayGroup } from '../lib/parcel'
import type { DashboardFilters, Parcel } from '../api/types'

type SectionKey = 'favorites' | 'announced' | 'active' | 'delivered' | 'archived'

const SECTION_DEFAULTS: Record<SectionKey, boolean> = {
  favorites: true,
  announced: true,
  active: true,
  delivered: true,
  archived: false,
}

const selectClass = `w-full sm:w-auto ${inputBase}`

const iconButton =
  'shrink-0 rounded-lg border border-slate-300 bg-white p-2 text-slate-600 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800'

/** Parcels on their way, split by expected day, in `DAY_GROUP_ORDER`; empty groups left out. */
function groupByDay(parcels: Parcel[]): [DayGroup, Parcel[]][] {
  const now = new Date()
  const groups = new Map<DayGroup, Parcel[]>()
  for (const parcel of parcels) {
    const key = dayGroup(parcel, now)
    groups.set(key, [...(groups.get(key) ?? []), parcel])
  }
  return DAY_GROUP_ORDER.filter((key) => groups.has(key)).map((key) => [key, groups.get(key)!])
}

const SECTION_STORAGE_KEY = 'parceltracker.sections'

/**
 * Which sections are open, remembered per browser.
 *
 * Storage can be missing or throw (private window, blocked site data); the board then just
 * starts from the defaults every time.
 */
function useSectionState(): [Record<SectionKey, boolean>, (key: SectionKey) => void] {
  const [open, setOpen] = useState<Record<SectionKey, boolean>>(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(SECTION_STORAGE_KEY) ?? '{}')
      return { ...SECTION_DEFAULTS, ...(typeof stored === 'object' && stored ? stored : {}) }
    } catch {
      return SECTION_DEFAULTS
    }
  })
  const toggle = (key: SectionKey) =>
    setOpen((current) => {
      const next = { ...current, [key]: !current[key] }
      try {
        localStorage.setItem(SECTION_STORAGE_KEY, JSON.stringify(next))
      } catch {
        // Not remembered, still toggled.
      }
      return next
    })
  return [open, toggle]
}

/**
 * Four sections, top to bottom: Unterwegs, Zugestellt, Angekündigt, Archiviert.
 *
 * "Zugestellt" is bounded to the last two weeks and therefore shown expanded; everything
 * older drops into the collapsed "Archiviert". Section membership and ordering come from
 * the backend, so the board is identical for every client and the rules stay testable.
 */
export function Dashboard() {
  const [filters, setFilters] = useState<DashboardFilters>({})
  const [search, setSearch] = useState('')
  const [openSections, toggleSection] = useSectionState()
  const [openParcel, setOpenParcel] = useState<number | null>(null)
  const [adding, setAdding] = useState(false)
  const [showFilters, setShowFilters] = useState(false)

  const activeFilters: DashboardFilters = { ...filters, q: search }
  const { data, isLoading, isError, refetch, isFetching } = useDashboard(activeFilters)
  // What wants someone today sits in its own strip on top and is not listed a second time
  // under "Unterwegs".
  const today = data?.active.filter(needsAttentionToday) ?? []
  const onTheWay = data?.active.filter((parcel) => !needsAttentionToday(parcel)) ?? []
  const { data: projects } = useProjects()
  const { data: carriers } = useCarriers()

  // Which carriers have no API, resolved once for the whole board rather than per row:
  // the carrier list is cached reference data, the parcel list is not.
  const mailOnlyCarriers = useMemo(
    () => new Set(carriers?.filter((c) => !c.has_api).map((c) => c.code) ?? []),
    [carriers],
  )
  const isMailOnly = (parcel: Parcel) => mailOnlyCarriers.has(parcel.carrier_code)

  const activeFilterCount = [
    filters.projectId != null || filters.unassigned,
    Boolean(filters.carrier),
    Boolean(filters.direction),
  ].filter(Boolean).length

  return (
    <div className="mx-auto max-w-5xl space-y-4 px-4 py-6">
      <h1 className="sr-only">{de.nav.dashboard}</h1>
      {/* Search and the two actions always in one row; the filters below it. On a phone
          the filters fold away behind a button, so the parcels start higher up. */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <div className="relative min-w-0 flex-1">
            <Search
              className="pointer-events-none absolute top-1/2 left-2.5 h-4 w-4 -translate-y-1/2 text-slate-400"
              aria-hidden
            />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder={de.actions.search}
              aria-label={de.actions.search}
              className={inputClass + ' pl-8'}
            />
          </div>

          <button
            type="button"
            onClick={() => setShowFilters((open) => !open)}
            aria-expanded={showFilters}
            aria-controls="dashboard-filters"
            className={`${iconButton} relative sm:hidden`}
            title={de.filters.toggle}
            aria-label={de.filters.toggle}
          >
            <SlidersHorizontal className="h-4 w-4" aria-hidden />
            {activeFilterCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-indigo-500 px-1 text-[10px] font-semibold text-slate-950">
                {activeFilterCount}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => void refetch()}
            title={de.actions.refresh}
            aria-label={de.actions.refresh}
            className={iconButton}
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} aria-hidden />
          </button>

          <button
            type="button"
            onClick={() => setAdding(true)}
            aria-label={de.actions.add}
            className={primaryButton + ' inline-flex shrink-0 items-center gap-1.5'}
          >
            <Plus className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">{de.actions.add}</span>
          </button>
        </div>

        <div
          id="dashboard-filters"
          className={`${showFilters ? 'grid' : 'hidden'} gap-2 sm:flex sm:flex-wrap sm:items-center`}
        >
          <select
            value={filters.unassigned ? 'none' : (filters.projectId ?? '')}
            onChange={(event) => {
              const value = event.target.value
              setFilters({
                ...filters,
                projectId: value && value !== 'none' ? Number(value) : null,
                unassigned: value === 'none',
              })
            }}
            className={selectClass}
          >
            <option value="">{de.filters.allProjects}</option>
            <option value="none">{de.filters.noProjectOnly}</option>
            {projects?.map((project) => (
              <option key={project.id} value={project.id}>
                {project.name}
              </option>
            ))}
          </select>
          {/* Returns share the sections with incoming parcels, marked; this narrows to one
                direction when someone is looking for "what are we still sending back". */}
          <select
            value={filters.direction ?? ''}
            onChange={(event) =>
              setFilters({
                ...filters,
                direction: (event.target.value || null) as DashboardFilters['direction'],
              })
            }
            className={selectClass}
          >
            <option value="">{de.filters.allDirections}</option>
            <option value="inbound">{de.filters.inboundOnly}</option>
            <option value="return">{de.filters.returnsOnly}</option>
          </select>
          <select
            value={filters.carrier ?? ''}
            onChange={(event) => setFilters({ ...filters, carrier: event.target.value || null })}
            className={selectClass}
          >
            <option value="">{de.filters.allCarriers}</option>
            {carriers?.map((carrier) => (
              <option key={carrier.code} value={carrier.code}>
                {carrier.name}
              </option>
            ))}
          </select>
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={() => setFilters({})}
              className="justify-self-start px-1 text-sm text-slate-500 underline-offset-2 hover:text-slate-800 hover:underline dark:text-slate-400 dark:hover:text-slate-200"
            >
              {de.filters.reset}
            </button>
          )}
        </div>
      </div>

      {isError && (
        <div className="rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">
          {de.errors.loading}{' '}
          <button onClick={() => void refetch()} className="underline">
            {de.errors.retry}
          </button>
        </div>
      )}

      {isLoading && !data ? (
        <div className="space-y-4">
          {[0, 1, 2].map((index) => (
            <div key={index} className="h-28 animate-pulse rounded-xl bg-white dark:bg-slate-900" />
          ))}
        </div>
      ) : (
        data && (
          <>
            <TodayStrip parcels={today} onOpen={setOpenParcel} />

            {/* Starred parcels first, above the life cycle -- they are why someone opened
                the board. They stay in their own section below as well, so the count in
                "Unterwegs" keeps meaning what it says. Hidden while nobody has starred
                anything, so an unused feature costs no space. */}
            {data.favorites_total > 0 && (
              <Section
                title={de.sections.favorites}
                hint={de.sections.favoritesHint}
                count={data.favorites_total}
                collapsible
                open={openSections.favorites}
                onToggle={() => toggleSection('favorites')}
              >
                <ul>
                  {data.favorites.map((parcel) => (
                    <ParcelRow
                      key={parcel.id}
                      parcel={parcel}
                      onOpen={setOpenParcel}
                      mailOnly={isMailOnly(parcel)}
                    />
                  ))}
                </ul>
              </Section>
            )}

            {/* Section order follows a parcel's life: announced, on its way, delivered. */}
            <Section
              title={de.sections.announced}
              hint={de.sections.announcedHint}
              count={data.announced_total}
              collapsible
              open={openSections.announced}
              onToggle={() => toggleSection('announced')}
            >
              <ul>
                {data.announced.map((parcel) => (
                  <ParcelRow
                    key={parcel.id}
                    parcel={parcel}
                    onOpen={setOpenParcel}
                    mailOnly={isMailOnly(parcel)}
                    dimmed
                  />
                ))}
              </ul>
            </Section>

            <Section
              title={de.sections.active}
              hint={de.sections.activeHint}
              weight="primary"
              count={data.active_total - today.length}
              collapsible
              open={openSections.active}
              onToggle={() => toggleSection('active')}
            >
              {/* Grouped by the day each parcel is expected, so the date is read once per
                  group instead of once per row. */}
              {groupByDay(onTheWay).map(([group, parcels]) => (
                <div key={group}>
                  <GroupHeading
                    title={de.dayGroups[group]}
                    count={parcels.length}
                    tone={group === 'overdue' || group === 'attention' ? 'warning' : 'default'}
                  />
                  <ul>
                    {parcels.map((parcel) => (
                      <ParcelRow
                        key={parcel.id}
                        parcel={parcel}
                        onOpen={setOpenParcel}
                        mailOnly={isMailOnly(parcel)}
                        inOverdueGroup={group === 'overdue'}
                      />
                    ))}
                  </ul>
                </div>
              ))}
            </Section>

            <Section
              title={de.sections.delivered}
              hint={de.sections.deliveredHint(data.delivered_section_days)}
              count={data.delivered_total}
              collapsible
              open={openSections.delivered}
              onToggle={() => toggleSection('delivered')}
            >
              <ul>
                {data.delivered.map((parcel) => (
                  <ParcelRow
                    key={parcel.id}
                    parcel={parcel}
                    onOpen={setOpenParcel}
                    mailOnly={isMailOnly(parcel)}
                  />
                ))}
              </ul>
            </Section>

            {/* The one unbounded section, so it keeps the collapsed-archive role. */}
            <Section
              title={de.sections.archived}
              hint={de.sections.archivedHint}
              weight="quiet"
              count={data.archived_total}
              collapsible
              open={openSections.archived}
              onToggle={() => toggleSection('archived')}
            >
              <ul>
                {data.archived.map((parcel) => (
                  <ParcelRow
                    key={parcel.id}
                    parcel={parcel}
                    onOpen={setOpenParcel}
                    mailOnly={isMailOnly(parcel)}
                    dimmed
                  />
                ))}
              </ul>
            </Section>
          </>
        )
      )}

      {openParcel != null && (
        <ParcelDrawer
          parcelId={openParcel}
          onClose={() => setOpenParcel(null)}
          onOpenParcel={setOpenParcel}
        />
      )}
      {adding && <AddParcelDialog onClose={() => setAdding(false)} />}
    </div>
  )
}
